import { type ReactNode, useMemo } from 'react';
import { Box } from '@mui/material';
import { useAppLayout } from '@core/contexts/LayoutContext';
import type { PageMetaProps } from '@core/interfaces';
import { ConfirmDialog } from '../dialogs';
import Page from './Page';
import PageAdd, { type PageAddConfig } from './PageAdd';
import PageFilter, { type PageFilterConfig } from './PageFilter';
import PageHeader from './PageHeader';
import { type PageSearchConfig } from './PageSearch';
import PageShell from './PageShell';

export interface CrudDeleteConfig<T> {
  title: string;
  item: T | null;
  getContent: (item: T) => string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

export type PageVariantProps = PageMetaProps & {
  variant?: 'page';
};

export type SectionVariantProps = {
  variant: 'section';
  title: string;
};

export type CrudPageLayoutProps<T> = {
  searchConfig?: PageSearchConfig;
  filterConfig?: PageFilterConfig;
  addConfig?: PageAddConfig;
  addEditDialog?: ReactNode;
  deleteConfig: CrudDeleteConfig<T>;
  children: ReactNode;
} & (PageVariantProps | SectionVariantProps);

function CrudPageLayout<T>({
  variant,
  searchConfig,
  filterConfig,
  addConfig,
  addEditDialog,
  deleteConfig,
  children,
  ...pageMeta
}: CrudPageLayoutProps<T>) {
  const { isWebTwo } = useAppLayout();
  const buttonSize = variant === 'section' || isWebTwo ? 'sm' : 'md';

  const action = useMemo(() => {
    if (!filterConfig && !addConfig) return null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {filterConfig ? (
          <PageFilter {...filterConfig} size={buttonSize} />
        ) : null}
        {addConfig ? <PageAdd {...addConfig} size={buttonSize} /> : null}
      </Box>
    );
  }, [filterConfig, addConfig, buttonSize]);

  const body = (
    <>
      {children}
      {addEditDialog}
      <ConfirmDialog
        open={Boolean(deleteConfig.item)}
        title={deleteConfig.title}
        content={
          deleteConfig.item ? deleteConfig.getContent(deleteConfig.item) : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        onConfirm={deleteConfig.onConfirm}
        onCancel={deleteConfig.onCancel}
        loading={deleteConfig.loading}
      />
    </>
  );

  if (variant === 'section') {
    return (
      <PageShell
        header={
          <PageHeader
            title={pageMeta.title ?? ''}
            action={action}
            searchConfig={searchConfig}
          />
        }
      >
        {body}
      </PageShell>
    );
  }

  return (
    <Page {...pageMeta} action={action} searchConfig={searchConfig}>
      {body}
    </Page>
  );
}

export default CrudPageLayout;
